/* The presenter: what carries 256x240 game pixels onto the glass.
 *
 * The game draws into an offscreen 2D canvas at exactly VWxVH and knows
 * nothing about this file. What happens between that buffer and the screen is
 * a display concern, and it was doing two things badly:
 *
 *   - UPSCALING BY CSS. `canvas.style.width = VW * scale` with
 *     `image-rendering: pixelated` is nearest-neighbour at whatever the final
 *     device-pixel ratio works out to. When that is not a whole number, some
 *     game pixels get four device pixels and some get five: measured on six
 *     of eight named phone geometries, e.g. 4px x245 and 5px x2 across one
 *     row. `src/scale.ts` avoids it by refusing every non-integer scale, and
 *     pays for that with a picture covering 24.6% of an iPhone SE.
 *
 *     A fragment shader does not have to choose. Sample on the texel grid and
 *     antialias the SEAM analytically — `fwidth` gives the width of one screen
 *     pixel in texels, so a game pixel becomes a solid block with a boundary
 *     exactly one screen pixel wide, at any scale. Every pixel is then the
 *     same size and the shimmer has nowhere to come from.
 *
 *   - NOTHING ELSE. Bright things in this game — glim, a lit brazier, the
 *     flame, the cache glimmering at night — were the same flat fill as a
 *     rock. Bloom is the one post effect that reads as light rather than as a
 *     filter, and it is what makes a 1px art style look lit instead of
 *     printed.
 *
 * WebGL2, falling back to WebGL1, falling back to painting the buffer
 * straight onto the visible canvas — which is exactly the old behaviour, so a
 * context loss or a device without WebGL loses the effects and nothing else.
 */

export const PRESET = {
  // everything off: the buffer, blitted. What the game looked like before.
  flat: { edge: 1, bloom: 0, grille: 0, vignette: 0, aberration: 0, wet: 0 },   // the EDGE keeps the pixel grid; with it off the presenter's sampler blurred every texel of the picture it says it blits (2026-09-20)
  // The default keeps pixel edges and restrained light; CRT effects live in tube.
  /* NO VIGNETTE IN THE SHIPPED PRESET.
   *
   * A vignette darkens by distance from the CENTRE OF THE FRAME, so the same
   * piece of world is a different colour depending on where the camera puts
   * it — and in a game you scroll, that means the world changes as you walk.
   * It is invisible over broken ground and obvious over a flat expanse:
   * reported from play as the ocean shader not respecting vertical scroll.
   *
   * Measured on 36,864 open-ocean pixels seen from two camera positions three
   * tiles apart, with the water pass's own scroll fault fixed: mean |RGB|
   * difference 0.38 shipped and 16.58 with the vignette turned back up to what
   * `tube` uses. The 2D buffer under all of it differs by exactly 0, which is
   * the proof that the frame is world-correct and the lens is what moves.
   *
   * AN EARLIER VERSION OF THIS NOTE QUOTED 25.1 AND 19.4 AND CONVICTED THE
   * VIGNETTE OF MOST OF THE FAULT. Those readings were taken across a frame
   * that still had the opening banner on it — a thing fixed to the SCREEN, so
   * comparing across a scroll lined world pixels up against a caption. It put
   * a floor of ~19 under every reading in the bisect and made six unrelated
   * effects look equally guilty. The vignette is a real fault of this kind and
   * belongs off; it was not the one being reported.
   *
   * `tube` keeps it: that preset is explicitly a CRT and someone choosing it
   * is asking for the glass. `crisp` is the default and should show the world,
   * not the television. */
  crisp: { edge: 1, bloom: 0.18, grille: 0, vignette: 0, aberration: 0, wet: 1 },
  // for someone who wants the tube
  tube: { edge: 1, bloom: 0.85, grille: 0.30, vignette: 0.40, aberration: 0.75, wet: 1 },
};

export const VERT = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

/* THE SEAM, ANTIALIASED.
 *
 * `uv * texSize` is the position in texels. `floor(uv) + 0.5` is the centre of
 * the texel we are in. `fwidth` is how many texels one screen pixel covers.
 * Pushing the sample point to the texel centre except within one screen pixel
 * of the boundary gives a hard-edged pixel with a single soft row on the seam
 * — which is what nearest-neighbour looks like when the scale IS an integer,
 * and what it should look like when it is not.
 *
 * At scale 1 (fw >= 1) it degenerates to plain bilinear, which is correct:
 * there is no room for an edge.
 */
/** How many steps a light's falloff is cut into. Low enough to read as bands
 *  rather than a gradient; high enough that a torch is not a bullseye.
 *
 *  Written into the shader as `BANDS.toFixed(1)`, never as `${BANDS}`: JS
 *  stringifies 5.0 as "5", and `floor(f * 5) / 5` on a float in GLSL ES 3.00
 *  leans on an implicit int-to-float conversion that is not worth relying on
 *  in a shader that has to compile on every phone GPU the game runs on. */
const BANDS = 5.0;

/** The most lights one frame may carry. Braziers, a dropped ember, the
 *  player's own magic and a blast all at once is comfortably inside this, and
 *  a fixed-size array keeps the shader branchless. */
const MAX_LIGHTS = 16;

/** The relief this game ships with: the world stays readable and the lights
 *  add form and warmth. Ambient is high and the key is gentle ON PURPOSE — a
 *  top-down game that hides its own floor is a different game and would need
 *  its difficulty re-tuned around it, so this pass shapes and never conceals.
 *  The darkness curve that would make it a lighting GAME is deliberately not
 *  here. */
const RELIEF_DEFAULT = {
  ambient: 0.72,   // was 0.86: half of every frame sat inside a 40-step band of 255 (LS-234); the modelled light now shows
  sunAmt: 0.34,   // was 0.20
  sun: [-0.45, -0.65, 0.62],   // from up and to the left, a touch in front
  tint: [1, 1, 1],
  relief: 1,
};

/* THE WORLD COMES IN AS A TEXTURE, NOT AS A COPY (2026-09-14). The 3D scene used to be handed to the game's 2D canvas
 * with `drawImage(sceneCanvas)` -- a WebGL surface read back into a 2D canvas, which the display then uploaded AGAIN as
 * its source. Measured on a throttled phone context: ONE of those calls cost 30 ms of a 46 ms frame, two thirds of it,
 * and on this desk's real GPU it is 2.3 ms of 10.5. So the scene's canvas is uploaded straight into this context and
 * composited here instead: the world in the rows below the readout, the game's own canvas (the readout, the wind's
 * ribbons, the rain -- and the sun's wash, which is a translucent sky over the whole world) over it by its alpha. The
 * upload gives STRAIGHT colour, not premultiplied -- UNPACK_PREMULTIPLY_ALPHA_WEBGL is false by default and the browser
 * un-premultiplies a canvas source -- so the blend is a plain mix. Adding `s.rgb` whole instead bleached the picture:
 * the sun's wash arrived at full strength over a world that had already been dimmed by (1 - a).
 * Every later pass -- bloom, the main pass, the edge -- samples the composite, so they see exactly what they saw when
 * the world arrived by copy. */
export const COMP_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_src;
uniform sampler2D u_world;
uniform float u_top;      // the readout's share of the frame; uv.y counts UP, so the readout is above 1 - u_top
uniform sampler2D u_hud; uniform float u_hasHud; uniform sampler2D u_ui; uniform float u_hasUi;   // u_ui: a full-frame inked screen (the title, the bag, the map), over everything   // the inked HUD, drawn at the glass's own size (2026-09-28): sampled smooth over the readout's rows
void main() {
  /* texel-exact on both: the game's canvas is 256 wide and the world may be drawn at the glass's own size (2026-09-27),
   * so a LINEAR tap here smeared the HUD across its enlargement and softened the world it was handed */
  vec2 ss = vec2(textureSize(u_src, 0)), ws = vec2(textureSize(u_world, 0));
  vec4 s = texelFetch(u_src, ivec2(min(v_uv * ss, ss - 1.0)), 0);
  float worldTop = 1.0 - u_top;
  if (v_uv.y < worldTop) {
    vec2 wuv = vec2(v_uv.x, v_uv.y / worldTop);
    vec3 w = texelFetch(u_world, ivec2(min(wuv * ws, ws - 1.0)), 0).rgb;
    if (u_hasHud > 0.5) s = texture(u_src, v_uv);   // the ink look: the game's own overlays over the world (rain, marks, words) sampled smooth, not as hard pixels
    outColor = vec4(mix(w, s.rgb, s.a), 1.0);
  } else if (u_hasHud > 0.5) {
    outColor = vec4(texture(u_hud, vec2(v_uv.x, (v_uv.y - worldTop) / max(u_top, 1e-4))).rgb, 1.0);
  } else {
    outColor = vec4(s.rgb, 1.0);
  }
  if (u_hasUi > 0.5) { vec4 u = texture(u_ui, v_uv); outColor.rgb = mix(outColor.rgb, u.rgb, u.a); }
}
`;

export const FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_src;
uniform sampler2D u_bloom;
uniform sampler2D u_water;
uniform vec2 u_texSize;
uniform vec2 u_hiSize;   // the composited picture's real texels: u_texSize times the world's scale (2026-09-27); every effect still counts in game pixels
uniform vec2 u_outSize;
uniform float u_edge, u_bloomAmt, u_grille, u_vignette, u_aberration, u_scale;
uniform float u_zoom;                     // the combat camera: 1 is the whole frame
uniform vec2 u_focus;                     // where it zooms about, in frame uv
uniform float u_wet, u_time, u_top;
uniform float u_inkLook;                  // 1 in the ink-and-watercolour look: the world is drawn at the glass's resolution, not in whole pixels
uniform float u_heat;                     // how hot the country on the glass is: 0 none, 1 the ash at noon
uniform vec4 u_firePos[${MAX_LIGHTS}];    // the fires on the glass: xy in frame pixels from the top, z radius, w intensity (2026-09-11)
uniform int  u_fireN;
uniform float u_fireAmt;                  // 0 switches the fire pass off, for the checks
// The horizontal seam between two screens during a vertical scroll, in
// texture coords, or -1.0 when only one screen is on the glass.
uniform float u_seam;
/** world position of the frame's top-left corner, in game pixels, so the
 *  surface pattern belongs to the water and not to the window */
uniform vec2 u_wave;

vec2 sharpen(vec2 uv) {
  vec2 t = uv * u_hiSize;       // where we are, in the picture's own texels
  vec2 f = fract(t);            // ...and where within this texel
  // one screen pixel, in texels. Guarded: fwidth is 0 on a degenerate quad.
  vec2 w = max(fwidth(t), vec2(1e-5));
  // Flat across the texel, ramping only within half a screen pixel of each
  // boundary. The first attempt at this was the same idea INVERTED — it
  // clamped to the texel BOUNDARY rather than its centre, so everything
  // beyond half a screen pixel from the middle was a 50/50 blend and the
  // whole picture went soft. Measured before believing: 4-game-pixel bars
  // came out 20-22 device pixels wide where nearest-neighbour gave 18-19.
  vec2 s = clamp(f / w, 0.0, 0.5) + clamp((f - 1.0) / w + 0.5, 0.0, 0.5);
  return (floor(t) + s) / u_hiSize;
}

/* WATER.
 *
 * THREE ATTEMPTS AT THIS WERE THE WRONG SHAPE OF EFFECT.
 *
 * The first two were a planar mirror: a pixel d below the surface samples the
 * frame 2d pixels above it. The geometry was right and it looked bad, twice,
 * for a reason that is not a tuning problem. A mirror is a SMEAR — it takes
 * 16px tiles of authored art and stretches them down a lake with a sine
 * displacement, which in a 36-colour pixel buffer reads as a smudge rather
 * than as a reflection. Then the weather laid on top of it was built from
 * sines of the whole surface, and a sine in y across a lake is a horizontal
 * stripe across a lake; three of them is a venetian blind.
 *
 * What pixel-art water is actually made of, per the reference this now
 * follows (jess-hammer/2d-pixel-water-shader):
 *
 *   - a DEPTH GRADIENT, not a flat fill: the colour steps as it gets deeper;
 *   - CAUSTICS — a slow web of bright lines, the single feature that says
 *     "water" more than anything else;
 *   - FOAM at the shoreline, broken rather than ruled;
 *   - SPECULAR: sparse thresholded glints;
 *   - and all of it in WORLD space and QUANTISED TO THE GAME PIXEL, so it
 *     travels with the lake through a screen transition and never lands on a
 *     half pixel. That quantisation is the whole reason it reads as art
 *     rather than as a filter over art.
 *
 * A trace of the mirror survives in the first few pixels below a bank, where
 * a real shore does hold a reflection of what stands on it — but it is a
 * detail on the shoreline now, not the effect.
 */

/** hash a lattice point to 0..1 */
float h11(vec2 p) {
  vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  q += dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
vec2 h22(vec2 p) { return vec2(h11(p), h11(p + 19.19)); }

/** Worley cell EDGES: the distance between the nearest and second-nearest
 *  feature point. Near zero on the boundaries between cells, which draws a
 *  web of thin lines — a caustic, without a texture to ship. */
float caustic(vec2 p, float t) {
  vec2 cell = floor(p), f = p - cell;
  float d1 = 8.0, d2 = 8.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 o = vec2(float(i), float(j));
      vec2 fp = h22(cell + o);
      // the feature points drift, each on its own phase, so the web crawls
      fp = 0.5 + 0.45 * sin(t * 0.7 + 6.2831 * fp);
      float d = length(o + fp - f);
      if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
    }
  }
  return d2 - d1;
}

vec3 water(vec2 uv, vec3 col) {
  vec4 m = texture(u_water, uv);
  if (m.r < 0.5) return col;
  float depth = m.g * 255.0;
  float deep = m.b;

  // ON THE GAME PIXEL GRID, IN THE WORLD. Everything below is computed from
  // this: no sub-pixel anything, and the pattern belongs to the lake rather
  // than to the window it is seen through.
  /* ...AND THE WORLD'S Y COUNTS THE OTHER WAY.
   *
   * Every texture here is uploaded with UNPACK_FLIP_Y_WEBGL, so uv.y counts UP
   * from the bottom of the frame, while u_wave is the window's world origin and
   * counts DOWN. Adding them moved the pattern by TWICE the scroll instead of
   * holding it still: walk south three tiles and every wave, caustic, shelf and
   * glint on the open sea slid six tiles the other way. uv.x is not flipped,
   * which is why this was only ever visible on the vertical axis -- reported
   * from play as the ocean shader not respecting vertical camera scroll.
   *
   * The relief pass below already converts, with texSize.y minus the snapped
   * row, and says why; this pass never got the same treatment. Everything the
   * water draws is measured off wp, so all five effects moved together and it
   * read as the whole sea sliding rather than as any one artefact. */
  vec2 wp = floor(uv * u_texSize);
  wp.y = u_texSize.y - wp.y;
  wp += u_wave;
  float t = u_time;

  // 1. DEPTH, AND THE CONTOUR IS NOT A RULED LINE.
  //
  // THIS WAS THE HORIZONTAL STREAKING. The mask measures depth by walking
  // DOWN each column from the water's top edge, so a contour of constant
  // depth is a horizontal line across the lake — and 'floor(depth / 7.0)'
  // drew up to three hard horizontal edges across every body of water. The
  // sines were taken out of this shader one round earlier for making stripes
  // and the step that replaced them made stripes a different way.
  //
  // The idea is still right: water is not one colour, and a smooth ramp in a
  // 36-colour picture is a gradient nothing else in the frame has. What has
  // to go is the straightness. Two octaves of world-space hash push the depth
  // the step is taken on, so the boundary wanders by several pixels and reads
  // as a shelf rather than as a rule — and the noise is blocky on the game
  // pixel, which is what the rest of the art does.
  float warp = h11(floor(wp / 5.0)) * 0.62 + h11(floor(wp / 11.0) + 17.0) * 0.38;
  float band = floor(clamp((depth + warp * 10.0 - 5.0) / 9.0, 0.0, 2.0));
  vec3 out3 = col * (1.0 - 0.045 * band);

  // 2. THE SHORELINE MIRROR, all that is left of the planar reflection: the
  // three pixels under a bank take a little of what stands on it.
  if (depth < 3.5) {
    vec2 ref = uv;
    ref.y -= (2.0 * depth + 1.0) / u_texSize.y;
    /* ...AND NEVER ACROSS THE SEAM.
     *
     * The mirror reads up to seven pixels ABOVE the water pixel, and during a
     * VERTICAL transition those seven pixels belong to the other screen. A
     * lake lying against the seam therefore reflected a strip of a different
     * place, sliding as the two screens travelled — reported from play as the
     * shader being pulled across a transition between two water screens, and
     * only ever on the vertical axis, because a horizontal scroll moves the
     * seam the one way this effect never samples.
     *
     * The seam uniform is where that boundary sits this frame; a sample that
     * would cross it is dropped, which costs the bottom three pixels of a lake
     * their shoreline glint for a few frames and costs the other screen
     * nothing at all. */
    /* ...AND IT IS CLAMPED AT THE TOP RATHER THAN DROPPED.
     *
     * The mirror reads up to seven pixels above the water pixel, and the frame
     * has nothing above its first row — so this used to skip the effect there.
     * That makes the glint a function of WHERE IN THE WINDOW the water sits:
     * the same stretch of sea loses its shoreline glint as it nears the top of
     * the screen and gets it back as you walk away, which reads as the ocean
     * changing when the camera scrolls and not when the world does. Reported
     * from play in exactly those words.
     *
     * NO MEASUREMENT BACKS THIS ONE, and an earlier version of this note
     * claimed one that did not survive re-measuring: on open ocean the clamp
     * moves nothing at all — 0.38 with it and 0.38 without — because water at
     * the top of the window is DEEP, and the mirror only runs under 3.5. It
     * bites on a shallow lake lying against the top of the frame, which the
     * ocean sweep never puts there. It stays because it is the more correct of
     * the two behaviours, not because it fixed the reported fault: that was the
     * flipped y in the wave origin above.
     *
     * Clamping samples one row too low at the very top instead of switching
     * the effect off. That is a pixel of inaccuracy in the top row; dropping it
     * makes the glint a function of where in the window the water sits. */
    bool crosses = u_seam >= 0.0 && ((uv.y >= u_seam) != (ref.y >= u_seam));
    ref.y = max(ref.y, u_top);
    if (!crosses) {
      vec3 r = texture(u_src, ref).rgb;
      out3 = mix(out3, mix(r, out3, 0.35) * 0.9, 0.30 * (1.0 - depth / 3.5));
    }
  }

  // 3. CAUSTICS. Two layers at different scales and speeds so the web is not
  // one regular mesh; thresholded hard, because a soft caustic is a haze.
  // ...FINE. At 0.085 the cells were ~12px across and the lake read as a
  // wall of green pebbles rather than as light on water: the web has to be
  // smaller than the things floating in it. Two octaves an octave apart, the
  // second at a third the weight, thresholded thin.
  float c = caustic(wp * 0.20, t)
          + 0.45 * caustic(wp * 0.38 + 31.0, -t * 0.8);
  float web = smoothstep(0.34, 0.10, c);          // bright ON the cell edges
  // ...and 'deep' is a per-TILE flag, so scaling anything by it directly puts
  // a 16px step wherever a shallow tile meets a deep one — horizontal too,
  // wherever that boundary runs along a row. Softened by the same warp.
  float depthMix = clamp(deep + (warp - 0.5) * 0.5, 0.0, 1.0);
  out3 += vec3(0.055, 0.085, 0.085) * web * (0.4 + 0.6 * depthMix);

  // 4. FOAM. A broken line in the first two pixels, cut by the same web so it
  // laps instead of ruling a border along the shore.
  if (depth < 2.5) {
    float lip = (1.0 - smoothstep(0.0, 2.5, depth));
    float broken = step(0.35, h11(floor(vec2(wp.x, 0.0) * 0.5) + floor(t * 2.5)));
    out3 += vec3(0.16, 0.20, 0.22) * lip * broken;
  }

  // 5. SPECULAR. Single pixels, sparse, each blinking on its own clock.
  vec2 id = floor(wp / 3.0);
  float on = step(0.90, h11(id + 7.3) * 0.45
    + 0.55 * (0.5 + 0.5 * sin(t * 1.8 + h11(id) * 12.0)));
  out3 += vec3(0.13, 0.15, 0.15) * on * (0.3 + 0.7 * depthMix);
  return out3;
}

/* THE RELIEF PASS, reading the normal buffer light.js bakes.
 *
 * Inert until the game hands one over: u_relief is 0 by default and lit()
 * returns 1.0, so every pixel is multiplied by one and nothing changes.
 * (No backticks in here, ever — this text lives inside a JS template
 * literal, and a backtick in a GLSL comment closes the shader.) */
uniform sampler2D u_normal;
uniform sampler2D u_height;               // the world as a height field, for shadows
uniform float u_persp;                   // PERSPECTIVE TOP-DOWN (prototype, 2026-09-13): screen scale per world pixel of height, 0 = orthographic
uniform float u_pitch;                     // ...and a pitch: how far up the glass (in uv) a pixel of height lifts a point
uniform vec2 u_perspC;                   // the point under the camera, in uv
uniform float u_shadow;                   // 0 disables the shadow march
uniform float u_rt;                       // shadow rays from the point lights, and contact shade
uniform vec4 u_lightPos[${MAX_LIGHTS}];   // xy = game pixels, z = radius, w = intensity
uniform vec3 u_lightCol[${MAX_LIGHTS}];
uniform int  u_lightN;
uniform float u_ambient;                  // floor, so the world stays readable
uniform vec3 u_tint;                      // the hour's colour on the ambient and the key
uniform vec3 u_sun;                       // fixed key direction
uniform float u_sunAmt;
uniform float u_relief;                   // 0 disables all of this
uniform float u_hud;                      // frame rows above this are the HUD: never lit

vec3 lit(vec2 px, vec2 texSize) {
  if (u_relief <= 0.0) return vec3(1.0);
  // the middle of the game pixel this falls in — everything below is measured
  // from here, which is what makes the result land on pixel boundaries
  vec2 snapped = floor(px) + 0.5;
  /* TWO SPACES, AND THEY DISAGREE ABOUT WHICH WAY IS DOWN.
   *
   * Every texture here is uploaded with UNPACK_FLIP_Y_WEBGL, so the shader's
   * y counts from the BOTTOM of the frame. The normal buffer's green channel,
   * the sun direction and the light positions the game hands over are all in
   * the game buffer's own coordinates, where y counts DOWN from the top of
   * the HUD. Sampling is done in the first space and geometry in the second.
   *
   * Getting this wrong is silent and total: a brazier's pool lands mirrored
   * about the middle of the screen, so it lights empty ground and the flame
   * itself stays dark. Measured before the fix, a lit brazier moved the mean
   * luminance within 30px of itself by +0.1; after, by +17.4. */
  vec2 cpx = vec2(snapped.x, texSize.y - snapped.y);
  if (cpx.y < u_hud) return vec3(1.0);   // the HUD is paper, not world; the hour does not tint it
  vec3 n = texture(u_normal, snapped / texSize).xyz * 2.0 - 1.0;
  n.z = abs(n.z);
  n = normalize(n + vec3(0.0, 0.0, 1e-5));

  // A KEY LIGHT THAT ONLY SHAPES. It is not allowed to darken anything below
  // the ambient floor: this pass exists to give the world form, and a top-down
  // game whose far walls go black stops being readable at exactly the moment
  // the player needs to read it.
  /* A SOFT TERMINATOR. Clamping the dot product at zero puts a hard line where a surface turns
   * away from the sun, which on a 13-pixel body is a seam down the middle of
   * it. Smoothed across the turn, the same light wraps the shape instead of
   * cutting it -- the half-lambert every stylised renderer uses, and the
   * reason a boulder reads as round rather than as two-tone. */
  float key = smoothstep(-0.15, 0.75, dot(n, normalize(u_sun)));
  /* CAST SHADOWS, BY MARCHING THE HEIGHT FIELD (LS-201).
   *
   * Vijay: "want to see if ray tracing or dynamic lighting and shadows would
   * work". This is the honest form of the question at this scale. A top-down
   * world IS a height field, so the ray from a pixel to the sun is a line
   * across that field, and anything standing higher than the ray along the
   * way is between this pixel and the light. Sixteen steps, and the ray
   * climbs as it goes -- so a low sun throws long shadows and a high one
   * throws almost none, out of the same two lines of arithmetic.
   *
   * The height buffer is built where the normals are, out of the geometry the
   * art is now made of, so a rock casts a rock's shadow rather than a
   * rectangle's. A u_shadow of zero skips the whole thing. */
  float shade = 0.0;
  if (u_shadow > 0.0) {
    // toward the sun, in the sampling space (its y counts the other way)
    vec2 dir = normalize(vec2(-u_sun.x, u_sun.y) + vec2(1e-5));
    float h0 = texture(u_height, snapped / texSize).r * 24.0;
    for (int i = 1; i <= 16; i++) {
      float t = float(i) * 1.6;
      vec2 sp = snapped + dir * t;
      float hh = texture(u_height, sp / texSize).r * 24.0;
      float ray = h0 + t * 0.42;
      /* SOFT SHADOWS COME OUT OF THE DISTANCE TO WHAT CASTS THEM.
       *
       * A hard edge is what you get when every blocker counts the same
       * however far away it is; in the world, the further the caster the
       * wider its penumbra, because the sun is not a point. Dividing the
       * blocked amount by the distance travelled is the cheap form of that:
       * a rock's own foot stays crisp and the far end of its shadow fades
       * out, which is the difference between a cut-out and a shadow. */
      float soft = 0.30 / (1.0 + t * 0.075);
      shade = max(shade, clamp((hh - ray) * soft, 0.0, 1.0));
    }
    // ...and it lands in bands, because everything else in this game does
    shade = floor(shade * 4.0 + 0.5) / 4.0;
  }
  /* A SHADOW TAKES THE KEY AND A BITE OF THE AMBIENT. Cutting the key alone
   * caps the effect at whatever the key is worth -- a fifth of the light in
   * this game's relief -- so a tree's shadow was a hint rather than a shadow.
   * The bite is small on purpose: this pass gives the world form and is not
   * allowed to hide its floor. */
  float dark = u_shadow * shade;
  /* CONTACT SHADE (LS-201). The cheapest true thing a height field can say:
   * look a few pixels out in six directions, and count how much of what is
   * around this pixel stands above it. Ground beside a rock is surrounded by
   * rock and goes dark; open ground is surrounded by nothing and does not.
   * This is ambient occlusion, and at six samples it costs less than one
   * light. */
  float ao = 0.0;
  if (u_rt > 0.0) {
    float h0 = texture(u_height, snapped / texSize).r * 24.0;
    for (int i = 0; i < 6; i++) {
      float a2 = float(i) * 1.0472;                       // sixty degrees apart
      vec2 o = vec2(cos(a2), sin(a2)) * 3.0;
      float hh = texture(u_height, (snapped + o) / texSize).r * 24.0;
      ao += clamp((hh - h0) * 0.16, 0.0, 1.0);
      /* (2026-09-12: a wider band at 8 px was tried for the terrace walls and taken out the same evening -- bodies stand in
       * the same height map, and it drew a dark ring round the player; Vijay: "what's this dark circle on my player".) */
    }
    ao = clamp(ao / 6.0, 0.0, 1.0) * u_rt;
  }
  vec3 acc = u_tint * (u_ambient * (1.0 - 0.16 * dark - 0.30 * ao) + u_sunAmt * key * (1.0 - dark));

  for (int i = 0; i < ${MAX_LIGHTS}; i++) {
    if (i >= u_lightN) break;
    vec4 L = u_lightPos[i];
    vec2 d = L.xy - cpx;
    float dist = length(d);
    if (dist > L.z) continue;
    /* THE FALLOFF IS DITHERED, NOT BANDED (LS-201).
     *
     * Reported from play: "fire has hard light rings". They were deliberate
     * once -- a quantised falloff put the light in five steps and the steps
     * were the look -- but a step in a RADIAL field is a ring, and once the
     * sun's shadows and the terminator went soft the rings were the only hard
     * thing left in the frame, drawing circles around every torch.
     *
     * The fix is the one pixel art has always used for a gradient: keep the
     * quantisation, and move each pixel's threshold by a little, in a pattern
     * fixed to the screen. A 4x4 ordered dither breaks each ring into a
     * stipple that reads as a smooth falloff at any zoom and never crawls,
     * because the pattern is a function of position and nothing else. The
     * bands are still there; they are just no longer circles.
     *
     * A smooth curve, too: light falls off with the square of distance, and
     * a linear ramp is a cone. */
    float f = 1.0 - dist / L.z;
    f = f * f * (3.0 - 2.0 * f);
    vec2 dp = mod(snapped, 4.0);
    float bayer = (
      mod(dp.y, 2.0) < 1.0
        ? (mod(dp.x, 2.0) < 1.0 ? (dp.x < 2.0 ? 0.0 : 8.0) : (dp.x < 2.0 ? 12.0 : 4.0))
        : (mod(dp.x, 2.0) < 1.0 ? (dp.x < 2.0 ? 3.0 : 11.0) : (dp.x < 2.0 ? 15.0 : 7.0))
    ) / 16.0;
    f = floor(f * ${BANDS.toFixed(1)} + bayer) / ${BANDS.toFixed(1)};
    // ...and the surface still has to face the light, so a boulder lit from
    // the left is bright on its left and dark on its right rather than
    // uniformly brighter
    vec3 ldir = normalize(vec3(d, L.z * 0.55));
    float lambert = max(dot(n, ldir), 0.0) * 0.75 + 0.25;
    /* A SHADOW RAY PER LIGHT (LS-201). The same march as the sun's, but toward
     * a point rather than a direction: step along the line to the lamp, and
     * if the field stands higher than the ray on the way, this pixel cannot
     * see it. That is what makes a torch throw a rock's shadow across the
     * ground and lose the far side of a wall, which no amount of falloff can
     * do on its own. Eight steps spread over the distance, so a near lamp is
     * sampled finely and a far one coarsely. */
    float vis = 1.0;
    /* ONLY THE NEAREST TWO LAMPS GET A RAY. The game hands its lights over
     * sorted by distance from the player, and a shadow ray is seven texture
     * reads: at sixteen lights that is a hundred and twelve reads a pixel,
     * which is a phone's whole frame. The two nearest are the two whose
     * shadows anyone can see. */
    if (u_rt > 0.0 && dist > 2.0 && i < 2) {
      float lh = 12.0;                                    // a lamp stands about this tall
      float h0 = texture(u_height, snapped / texSize).r * 24.0;
      vec2 stepv = vec2(d.x, -d.y) / 8.0;                 // into sampling space
      for (int k = 1; k <= 7; k++) {
        vec2 sp = snapped + stepv * float(k);
        float hh = texture(u_height, sp / texSize).r * 24.0;
        float ray = mix(h0, lh, float(k) / 8.0);
        vis = min(vis, 1.0 - clamp((hh - ray) * 0.22, 0.0, 1.0));
      }
      vis = mix(1.0, vis, u_rt);
    }
    acc += u_lightCol[i] * (f * L.w * lambert * vis);
  }
  return acc;
}

/* THE WORLD UNDER A PERSPECTIVE CAMERA (Vijay, 2026-09-13: "how much would we benefit from actually rendering the world
 * in 3d... it would make the depth between different elevations easier to read"). The camera stays square to the
 * world -- north up, x to x -- but stops being orthographic: a point at height h projects to
 *   s = c + (p - c) * (1 + h * K) + (0, h * T)
 * scaled about the point under the camera and lifted by the pitch. Inverting that per pixel is a march down the
 * height field: from the top height to the ground, the first h whose terrain stands as high as the ray is the hit.
 * A hit where the terrain stands well above the ray is a WALL seen from the side, which an orthographic camera
 * could never show; it is returned as the third value so the caller can shade it as a face. */
vec4 persp(vec2 uv) {
  if (u_persp <= 0.0 && u_pitch <= 0.0) return vec4(uv, 0.0, 1.0);
  vec2 c = u_perspC;
  /* ...RELATIVE TO THE GROUND UNDER THE CAMERA: the camera hangs over the body, so the body's own terrace is drawn at
   * its true scale and the ground below it shrinks; scaling from sea level magnified a high plateau by half about
   * the centre (the shell's lair stands at the top terrace: a bowl, 2026-09-13). */
  float h0 = texture(u_height, c).g * 24.0;
  for (int i = 0; i < 12; i++) {
    float h = 23.0 - float(i) * 2.0;   // two world pixels a step: a terrace is eight, and half the samples of one a step
    float dh = h - h0;
    vec2 p = c + (uv - vec2(0.0, dh * u_pitch) - c) / max(0.6, 1.0 + dh * u_persp);
    // past the world's edge (below the picture, or into the readout) there is no terrain: the ray falls to the ground
    if (p.y < 0.0 || p.y > 1.0 - u_top || p.x < 0.0 || p.x > 1.0) continue;
    float H = texture(u_height, p).g * 24.0;   // the ground's own height (green), not the props' (red)
    // a hit: a wall only where the ground stands more than a step above the ray (a plateau's top is within a step)
    if (H >= h) { float wall = clamp((H - h - 2.0) / 3.0, 0.0, 1.0); return vec4(p, wall, H > 0.0 ? h / H : 1.0); }
  }
  return vec4(uv, 0.0, 1.0);
}
void main() {
  /* THE COMBAT CAMERA (LS-203). Vijay: "can we add camera shake and slight
   * zoom during combat". The shake was already the game's (it jolts the
   * canvas); the zoom is the presenter's, because a zoom is a question about
   * how the frame is put on the glass. A magnification about a point, a few
   * percent at most, eased in by the game while a swing is out and eased
   * back when the fight is over. The grille and the vignette stay locked to
   * the screen, because they are the screen. */
  vec2 zv = u_focus + (v_uv - u_focus) / max(u_zoom, 1.0);
  vec2 uv = mix(zv, sharpen(zv), u_edge);

  /* THE HEAT (2026-09-10; Vijay: "can we properly simulate it with a
   * displacement shader effect"). Air over hot ground has a different
   * refractive index, so what you see through it is DISPLACED -- the picture
   * behind the heat wobbles. Nothing is drawn: the frame is sampled from
   * somewhere slightly to the side, and the eye reads the wobble as heat.
   *
   * Two things about it are pixel-art rules rather than optics:
   *
   * WHOLE SOURCE PIXELS. The frame is 256 wide and sampled NEAREST; a
   * fractional offset would resample the art and read as a blur, which is the
   * one thing a picture of hard pixels must not do. The offset is floored to
   * whole pixels of the source, so a column of the picture is shifted one or
   * two pixels sideways and stays exactly itself.
   *
   * AND IT BELONGS TO THE WORLD, NOT THE GLASS. The wobble's phase is taken
   * from the frame row plus the window's own world origin -- u_wave, the same
   * number the lake's ripple uses -- so walking does not drag the shimmer
   * along with the camera.
   *
   * Strongest low in the frame and fading upward, because that is where the
   * ground it comes off is. */
  float toHud = clamp(((1.0 - u_top) - uv.y) / 0.04, 0.0, 1.0);   // u_top is the readout's share of the frame, and uv.y counts UP, so the readout is above 1 - u_top
  if (u_heat > 0.0) {
    /* THE HUD DOES NOT WOBBLE, and the rest of the frame does.
     *
     * WHICH END IS WHICH WAS MEASURED, NOT REASONED. Reported from play as
     * "it's displacing the hud too", and both of my first two guesses at the
     * cutoff were wrong -- I read the water pass's note about its flipped
     * TEXTURES as being about the frame, then over-corrected. One experiment
     * settled it: displace only uv.y < 0.2 and count which end of the picture
     * moves. 20,834 pixels of the world moved and none of the readout, so
     * uv.y counts UP from the bottom and the readout is everything above
     * 1 - u_top, u_top being the share of the frame it takes.
     *
     * IT IS NOT A GRADIENT DOWN THE GLASS. The first cut faded the shimmer
     * from the bottom of the window upward, which looks right in a still and
     * is a screen-space effect: walk north and the haze slides down the
     * picture, because it was pinned to the window rather than to the ground.
     * Hot ground is hot everywhere, so the amount is flat and the PATCHINESS
     * comes from the world -- a slow hash of the world position, so a
     * shimmering patch stays over the ground it belongs to. */
    float wy = floor(uv.y * u_texSize.y) + u_wave.y;
    float wx = floor(uv.x * u_texSize.x) + u_wave.x;
    /* THE WORD patch IS RESERVED IN GLSL and cost a clip: the shader stopped
     * compiling the moment it appeared, the present pass fell back, and the
     * recording that went out had no shimmer in it at all. */
    float blotch = 0.55 + 0.45 * sin(wx * 0.035 + wy * 0.021 + u_time * 0.35);
    float band = u_heat * toHud * blotch;
    if (band > 0.001) {
      /* Three sines, a decade apart in pace and tied to the world row, so a
       * column does not shimmer as one piece: heat is not a wave. */
      float wob = sin(wy * 0.55 + u_time * 3.1) * 0.6
                + sin(wy * 0.21 - u_time * 1.7 + wx * 0.05) * 0.4
                + sin(wy * 1.30 + u_time * 5.3) * 0.25;
      float px = floor(wob * band * 2.6 + 0.5);          // whole pixels: at most two, and usually one
      /* IN INK IT IS A WAVER, NOT A TEAR (2026-09-28, Vijay: "the post processing effects like heat are looking really bad
       * now"). A whole source pixel is four on the glass once the world is drawn at the glass's size, so every row of a
       * painted tree or the hero was cut and slid sideways in strips. The ink look bends the air instead: a continuous
       * offset under a third of a game pixel, slow in both directions, so an edge sways and nothing is sliced. */
      if (u_inkLook > 0.5) { float wy2 = uv.y * u_texSize.y + u_wave.y, wx2 = uv.x * u_texSize.x + u_wave.x;
        px = (sin(wy2 * 0.19 + wx2 * 0.03 + u_time * 1.6) * 0.6 + sin(wy2 * 0.07 - u_time * 0.9 + wx2 * 0.02) * 0.4) * band * 0.3; }
      uv.x += px / u_texSize.x;
      uv = clamp(uv, vec2(0.0), vec2(1.0));
    }
  }
  /* FIRE IS PROMINENT (2026-09-11; Vijay: "when things are on fire the fire
   * needs to be more prominent, create a special shader for this"). Two
   * things a flame does to the air and the ground that a sprite cannot: the
   * air above it shimmers, and the ground around it is lit warm and unsteady.
   * The fires are the lights the relief pass already knows, tagged; this is a
   * pass of their own over the frame. The shimmer is whole pixels, like the
   * heat's, and only ABOVE the flame -- fp.y counts from the top, so above is
   * a smaller y. */
  vec2 fp = vec2(uv.x * u_texSize.x, (1.0 - uv.y) * u_texSize.y);
  // the terrain point this pixel sees under the perspective camera (the readout above 1 - u_top is flat glass)
  vec4 rp = uv.y < 1.0 - u_top ? persp(uv) : vec4(uv, 0.0, 1.0);
  vec2 uvw = rp.xy;
  float fireLit = 0.0, fireCore = 0.0;
  if (u_fireAmt > 0.0) {
    for (int i = 0; i < ${MAX_LIGHTS}; i++) {
      if (i >= u_fireN) break;
      vec4 F = u_firePos[i];
      vec2 d = fp - F.xy;
      float dist = length(d);
      if (dist > F.z) continue;
      float near = 1.0 - dist / F.z;
      float flick = 0.82 + 0.18 * sin(u_time * 11.0 + float(i) * 2.7) * sin(u_time * 4.3 + float(i));
      float above = clamp(-d.y / (F.z * 0.7), 0.0, 1.0) * clamp(1.0 - abs(d.x) / (F.z * 0.35), 0.0, 1.0);   // a column over the flame, not the whole glow
      float wob = sin(fp.y * 0.7 + u_time * 6.2 + float(i)) * 0.6 + sin(fp.y * 0.23 - u_time * 2.9) * 0.4;
      float px2 = floor(wob * above * near * 1.4 * toHud + 0.5);
      if (u_inkLook > 0.5) px2 = wob * above * near * 0.45 * toHud;   /* ink: a continuous waver over the flame, not whole-pixel strips */
      uv.x += px2 / u_texSize.x;
      fireLit += near * near * F.w * flick;
      fireCore += pow(clamp(1.0 - dist / (F.z * 0.24), 0.0, 1.0), 2.0) * F.w * flick;
    }
    uv = clamp(uv, vec2(0.0), vec2(1.0));
    fp = vec2(uv.x * u_texSize.x, (1.0 - uv.y) * u_texSize.y);
  }
  vec3 col;
  if (u_aberration > 0.0) {
    // A THIRD OF A GAME PIXEL, not a screen pixel: on a phone at scale 4 a
    // one-screen-pixel split is invisible, and on a desktop at scale 3 a
    // one-game-pixel split is a smear. Tie it to the art, not the hardware.
    vec2 off = vec2(u_aberration / (u_texSize.x * 3.0), 0.0);
    col.r = texture(u_src, uvw + off).r;
    col.g = texture(u_src, uvw).g;
    col.b = texture(u_src, uvw - off).b;
  } else {
    col = texture(u_src, uvw).rgb;
  }
  if (u_fireAmt > 0.0 && fireLit > 0.0) {
    // warm light on the ground, an unsteady white heart, the HUD untouched
    col += (vec3(1.0, 0.52, 0.18) * fireLit * 0.42 + vec3(1.0, 0.86, 0.6) * fireCore * 0.32) * u_fireAmt * toHud;
    col = min(col, vec3(1.0));
  }

  // the surface, before the light: a reflection is part of the picture, not a
  // glow over it
  if (u_wet > 0.0) col = mix(col, water(uvw, col), u_wet);

  /* THE RELIEF PASS. Multiplied, not added: this shapes what is already
   * there — a boulder's lit top against its shaded side — and a light that
   * ADDS would wash the palette out towards white, which is the one thing a
   * 36-colour picture cannot survive. lit() is 1.0 everywhere until the game
   * hands over a normal buffer, so this line costs nothing until then. */
  col *= lit(uvw * u_texSize, u_texSize);
  /* HEIGHT HAS A TONE (2026-09-12; Vijay: "depth confusion"). Two plateaus a terrace apart were the same colour and
   * only the cliff between them said which was up; the higher ground is lit a little more, the lower a little less. */
  { float hgt = texture(u_height, (floor(uvw * u_texSize) + 0.5) / u_texSize).r; col *= 0.84 + 0.30 * hgt; }
  /* A FACE SEEN FROM THE SIDE under the perspective camera: not the plateau's texture stretched (the first cut, a
   * striped smear) but rock -- the plateau's colour drained toward grey, lit at its lip and dark at its foot. */
  if (rp.z > 0.0) { float lum = dot(col, vec3(0.3, 0.59, 0.11)); vec3 face = (vec3(0.30, 0.27, 0.32) + 0.25 * vec3(lum) + 0.1 * col) * (0.6 + 0.7 * rp.w); col = mix(col, face, rp.z); }   // a rock tone of its own, so the plateau's texture does not stripe the face

  /* Light, added rather than mixed, so nothing that was dark gets lighter —
   * but SCREENED rather than summed, so nothing that was already bright is
   * pushed past white. A straight add pinned the green channel at 255 across
   * the light grass: measured, 7.96% of the map's pixels had a channel hard
   * clipped, which destroys every bit of texture in that channel and leaves
   * the brightest country as a flat neon slab with no highlight left to give
   * it. Screening keeps the same lift at the bottom of the range and rolls
   * off at the top, so the highlights stay inside the palette. */
  // Glow follows the same logical pixels as the surface lighting, not the
  // output grid. Continuous sampling reintroduced subpixel bands at bright edges.
  vec2 glowUV = (floor(zv * u_texSize) + 0.5) / u_texSize;
  vec3 lit = texture(u_bloom, glowUV).rgb * u_bloomAmt;
  col = 1.0 - (1.0 - clamp(col, 0.0, 1.0)) * (1.0 - clamp(lit, 0.0, 1.0));

  // THE GRILLE IS PER GAME PIXEL, not per screen pixel. A scanline at device
  // resolution on a 4x upscale is a 25%-duty comb that eats a quarter of the
  // picture; on the game's own grid it reads as the shape of the pixels.
  if (u_grille > 0.0 && u_scale >= 3.0) {
    vec2 g = fract(v_uv * u_texSize);
    float line = smoothstep(0.0, 0.5, g.y) * smoothstep(1.0, 0.5, g.y);
    col *= 1.0 - u_grille * (1.0 - line) * 0.5;
  }

  if (u_vignette > 0.0) {
    vec2 d = v_uv - 0.5;
    float v = 1.0 - u_vignette * dot(d, d) * 1.6;
    col *= v;
  }
  /* AND THE FRAME LANDS BACK ON STEPS, or the tube eats the palette.
   *
   * Everything above — the bloom, the grille, the vignette, the aberration —
   * is continuous maths applied per screen pixel to art that is 16 pixels to a
   * tile. Measured on one screen: the game's own 2D buffer holds 416 unique
   * colours, which is palette art; the same frame after this shader holds
   * 67,845. A flat stretch of road came back with 452 shades of itself, all
   * within three units, and every material carried the identical one-pixel
   * light/dark comb the grille leaves behind.
   *
   * None of that is visible as detail — it is below the threshold where the
   * eye reads texture — but it is exactly what stops the hard colour
   * boundaries this idiom is built on. Quantising at the end keeps the shape
   * of every effect, since a vignette that darkens by a fifth still darkens by
   * a fifth, while putting the result back onto a ladder of discrete values.
   *
   * The step is per channel and deliberately not tiny: coarse enough that a
   * flat fill comes out flat, fine enough that a gradient still reads as one. */
  /* A GRADE BEFORE THE LADDER (2026-09-05, against the reference renders, whose ground reads as sunlit and whose
   * shadows read as cool). A gentle S-curve on the value and a little more colour in what is already coloured: the
   * lit half of the picture warms, the shaded half cools, and the picture stops reading as one flat wash of sand.
   * Held small on purpose -- this runs under every effect above and must not take the palette anywhere new. */
  {
    float l = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(l), col, 1.14);                        // a touch more colour where there is colour
    col = clamp(col, 0.0, 1.0);
    col = col * col * (3.0 - 2.0 * col) * 0.34 + col * 0.66;   // an S-curve, a third of the way
    /* ONE CONTINUOUS BLEND, not two clamped windows (the art lens, round 49: the two clamps met at a luma of about a
     * half, and where a slow wash crossed that value the whole screen changed temperature along a straight line --
     * the same row on two unrelated seeds). A smooth curve over a wide range has no such edge anywhere. */
    float t = smoothstep(0.22, 0.82, l);
    col = mix(col * vec3(0.95, 0.97, 1.07), col * vec3(1.04, 1.0, 0.94), t);
  }
  /* AND THE LADDER IS CLIMBED WITH A DITHER (the art lens, round 49: 621 of 1028 columns of open fen changed value
   * across one screen row, at the same row on two unrelated seeds -- a contour, not terrain). The ladder was written
   * for flat palette fills, whose values already sit on a step; the sun's wash is the first smooth gradient in the
   * pipeline and a smooth ramp through a 1/26 step contours wherever it crosses one. An ordered 4x4 threshold moves
   * each pixel by up to half a step before the floor: a flat fill is unmoved, a ramp breaks into palette pixels. */
  const float LEVELS = 26.0;
  /* ...BUT NOT OVER A WORLD DRAWN AT THE GLASS'S OWN SIZE (2026-09-27, art direction B): the ladder and its ordered
   * dither live on the game's pixel grid, and over the smooth gradients of the full-resolution world they were a
   * checker across everything. The pixel art (?ss=1) keeps them. */
  if (u_hiSize.x > u_texSize.x * 1.5) { outColor = vec4(clamp(col, 0.0, 1.0), 1.0); return; }
  {
    // Quantize on the art grid, not the device grid. World grain travels with
    // the terrain; HUD grain stays fixed to its own source pixels.
    vec2 pixel = floor(vec2(zv.x, 1.0 - zv.y) * u_texSize);
    if (zv.y < 1.0 - u_top) pixel += floor(u_wave);
    ivec2 q = ivec2(mod(pixel, 4.0));
    int bi = q.x + q.y * 4;
    float m[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
    float d = (m[bi] / 16.0 - 0.46875) / LEVELS;
    col = clamp(col + d, 0.0, 1.0);
  }
  col = floor(clamp(col, 0.0, 1.0) * LEVELS + 0.5) / LEVELS;
  outColor = vec4(col, 1.0);
}`;

/** Bright-pass and blur in one: a small separable kernel over the source,
 *  keeping only what is already bright. Run at quarter resolution, which is
 *  what makes a nine-tap blur cost nothing. */
export const BLOOM_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_src;
uniform vec2 u_texel;
uniform float u_threshold;

vec3 bright(vec2 uv) {
  vec3 c = texture(u_src, uv).rgb;
  // luma, and only the part above the knee — so the glim and the flame bloom
  // and the ground does not
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  return c * smoothstep(u_threshold, u_threshold + 0.25, l);
}

void main() {
  vec3 sum = bright(v_uv) * 0.25;
  sum += bright(v_uv + vec2( u_texel.x, 0.0)) * 0.125;
  sum += bright(v_uv + vec2(-u_texel.x, 0.0)) * 0.125;
  sum += bright(v_uv + vec2(0.0,  u_texel.y)) * 0.125;
  sum += bright(v_uv + vec2(0.0, -u_texel.y)) * 0.125;
  sum += bright(v_uv + u_texel) * 0.0625;
  sum += bright(v_uv - u_texel) * 0.0625;
  sum += bright(v_uv + vec2( u_texel.x, -u_texel.y)) * 0.0625;
  sum += bright(v_uv + vec2(-u_texel.x,  u_texel.y)) * 0.0625;
  outColor = vec4(sum, 1.0);
}`;

/* THE NAMES THE JS BINDS, next to the shader that declares them. A uniform
 * called `u_bloom` was declared as BOTH a sampler and a float in the same
 * fragment shader; it compiled to `'u_bloom' : redefinition` at runtime, on a
 * device, and the only thing that caught it was loading the page. Two lists
 * one file apart is a drift waiting to happen — a check compares these
 * against the `uniform` declarations in the source above. */
export const PRESENT_UNIFORMS = ['u_src', 'u_bloom', 'u_water', 'u_texSize', 'u_hiSize',
  'u_outSize', 'u_edge', 'u_bloomAmt', 'u_grille', 'u_vignette', 'u_aberration',
  'u_scale', 'u_zoom', 'u_focus', 'u_wet', 'u_time', 'u_top', 'u_wave', 'u_seam', 'u_heat', 'u_inkLook', 'u_firePos', 'u_fireN', 'u_fireAmt',
  'u_persp', 'u_pitch', 'u_perspC',
  // ...and the relief pass. Same list-next-to-the-shader rule as the rest:
  // a check compares these against the `uniform` lines in the source above.
  'u_normal', 'u_height', 'u_shadow', 'u_rt', 'u_lightPos', 'u_lightCol', 'u_lightN', 'u_ambient', 'u_sun',
  'u_sunAmt', 'u_relief', 'u_tint', 'u_hud'];
export const BLOOM_UNIFORMS = ['u_src', 'u_texel', 'u_threshold'];

/** The size to give the visible canvas, in device pixels, and the scale that
 *  implies. A pure function so it can be checked without a browser.
 *
 *  The old rule refused every non-integer scale, because nearest-neighbour at
 *  a fractional scale is two pixel sizes on the glass. With the shader doing
 *  the upscale that constraint is gone: the seam is antialiased, so any scale
 *  is uniform. The rule becomes "fill what you are given, at whole device
 *  pixels, keeping the aspect", which is what a display should do. */
export function fitDevice(availCssW, availCssH, dpr, vw, vh, budget = 1000000) {
  /* THE PRESENTER'S PIXELS ARE THE PHONE'S WHOLE COST (Vijay, twice: "lagging
   * a ton on mobile", "still lagging a fair bit"). The lighting shader reads
   * about thirty texels a fragment, and at a phone's three device pixels per
   * CSS pixel a 390-pixel-wide screen is 1.3 million fragments. Two device
   * pixels per CSS pixel is all a 256x240 picture can use, and the fragment
   * count is capped at about a million on top of that: on a desk nothing
   * changes, on a phone the fill is halved or better. */
  /* A THIRD REPORT ("mobile is still lagging significantly"), AND THIS TIME
   * MEASURED AT THE RUNG THAT MATTERS. A million fragments is still too many.
   * Pinned at quality 1 -- the rung that draws every frame, and the one the
   * phone must hold to feel smooth -- with nothing else changed but the size
   * of the canvas being presented:
   *
   *      780x804  627k fragments   33.3 ms a frame   30 fps
   *      585x603  353k fragments   16.7 ms a frame   60 fps
   *      512x528  270k fragments   16.7 ms a frame   60 fps
   *
   * The game's own work already fits: update 0.8 ms, render 10.3, present 4.1,
   * 15.2 ms of a 16.7 ms budget. The other eighteen milliseconds were the
   * browser compositing this surface, which is why no amount of making the
   * game cheaper moved it.
   *
   * I MEASURED THIS ONCE BEFORE AND THREW IT AWAY, because I A/B'd it at rung
   * 0 -- where `DrawClock` halves the draw rate, so the surface is paid for 30
   * times a second instead of 60 and the difference hides. A performance test
   * at the wrong quality level is a performance test of nothing.
   *
   * The floor is two device pixels per game pixel: below that the presenter
   * invents detail the 256-wide picture never had, so `d` may fall under 1 on
   * a big tablet but the surface never goes under 2x the game. */
  let d = Math.min(2, Math.max(1, dpr || 1));
  const scale = Math.min(availCssW / vw, availCssH / vh);
  { const cssW0 = Math.max(vw, Math.floor(scale * vw)), cssH0 = cssW0 * (vh / vw);
    const floorD = Math.min(d, (2 * vw) / cssW0);
    while (d > floorD && cssW0 * d * cssH0 * d > budget) d = Math.max(floorD, d - 0.25); }
  // ...AND A CSS BOX BLINK CAN ACTUALLY EXPRESS. Layout is quantised to 1/64
  // of a CSS pixel, so `style.width = '341.3333px'` becomes 341.328125 — a
  // deficit of 0.016 device pixels, which is what the old path handed to two
  // columns of a 1024-pixel row. Snap the CSS box DOWN to a 64th first and
  // derive the backing store from that, so the browser is never asked to
  // resample our output and the framebuffer is a whole number of device
  // pixels of a box it can lay out exactly.
  const cssW = Math.floor(vw * scale * 64) / 64;
  const devW = Math.max(vw, Math.round(cssW * d));
  const devH = Math.max(vh, Math.round(devW * (vh / vw)));
  return { devW, devH, cssW, cssH: (devH / devW) * cssW, scale: cssW / vw, dpr: d };
}

/** What the presenter may spend on fragments. A phone pays for every one twice
 *  -- once in our shader, once in the compositor -- and it is the phone, not
 *  the desk, that has been reported lagging three times. A desk keeps the
 *  million it had and does not change. */
/** A phone, by the only thing a browser will tell us about it. Named once
 *  because two decisions depend on it: how many pixels to fill, and which
 *  quality rung to run at. */
export function coarsePointer() {
  return typeof matchMedia === 'function' && matchMedia('(hover: none), (pointer: coarse)').matches;
}
export function touchBudget() {
  return coarsePointer() ? 360000 : 1000000;
}

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error('shader: ' + log);
  }
  return s;
}

function program(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.bindAttribLocation(p, 0, 'a_pos');
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error('link: ' + gl.getProgramInfoLog(p));
  }
  return p;
}

export class Display {
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  bloomFbo: any;
  bloomH: any;
  bloomProg: any;
  bloomTex: any;
  bloomW: any;
  buffer: any;
  ctx2d: any;
  fallback: any;
  fit: any;
  frame: any;
  gl: any;
  lights: any;
  normalDirty: any;
  heightMap: any;
  perspK = 0; perspT = 0;   // the perspective prototype's knobs (0: the orthographic camera the game has always had)
  heightDirty: any;
  heightTex: any;
  normalMap: any;
  normalTex: any;
  ok: any;
  opts: any;
  preset: any;
  prog: any;
  reason: any;
  relief: any;
  seam: any;
  srcTex: any;
  compProg: any; compTex: any; compFbo: any; compW = 0; compH = 0;   // the world-and-readout composite (2026-09-14)
  worldTex: any; worldSrc: any = null;
  topOfWorld: any;
  u: any;
  ub: any;
  visible: any;
  waterDirty: any;
  waterMask: any;
  heat: number = 0;   // the country's heat shimmer, 0 to 1 (2026-09-10)
  worldEffects = true;
  fireAmt: number = 1;   // the fire pass's strength; 0 for the checks' control (2026-09-11)
  waterTex: any;
  waveX: any;
  waveY: any;


  /** @param {HTMLCanvasElement} visible  the canvas on the page
   *  @param {HTMLCanvasElement} buffer   the game's own VWxVH 2D canvas */
  constructor(visible, buffer, preset = 'crisp') {
    this.visible = visible;
    this.buffer = buffer;
    this.opts = { ...PRESET[preset] || PRESET.crisp };
    this.preset = PRESET[preset] ? preset : 'crisp';
    this.ok = false;
    this.reason = '';
    try { this.init(); } catch (err) { this.reason = String(err && err.message || err); }
    // A CONTEXT LOSS IS NOT A CRASH. It happens on a phone that backgrounds
    // the tab, and the presenter simply becomes the blitter it falls back to.
    visible.addEventListener?.('webglcontextlost', (e) => {
      e.preventDefault();
      this.ok = false;
      this.reason = 'context lost';
      this.ctx2d = null;
      this.fallback = null;
    });
    visible.addEventListener?.('webglcontextrestored', () => {
      // the stand-in goes away and the real canvas is shown again
      if (this.fallback && this.fallback.parentNode) {
        this.fallback.parentNode.removeChild(this.fallback);
      }
      this.fallback = null; this.ctx2d = null;
      this.visible.style.visibility = '';
      try { this.init(); } catch (err) { this.reason = String(err); }
    });
  }

  init() {
    const gl = this.visible.getContext('webgl2', {
      alpha: false, antialias: false, depth: false, stencil: false,
      premultipliedAlpha: false, preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('no webgl2');
    this.gl = gl;
    this.prog = program(gl, VERT, FRAG);
    this.bloomProg = program(gl, VERT, BLOOM_FRAG);
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.srcTex = this.mkTex(gl);
    this.compProg = program(gl, VERT, COMP_FRAG);
    this.compTex = this.mkTex(gl);
    this.compFbo = gl.createFramebuffer();
    this.compW = -1; this.compH = -1;   /* THE SIZE BELONGS TO THE OBJECTS (2026-09-17). init() is what the webglcontextrestored handler re-runs, and the fresh compTex has no storage and the fresh compFbo no attachment. The remembered size said otherwise, so draw() skipped both and the world presented black for the rest of the session. */
    this.worldTex = this.mkTex(gl);
    gl.bindTexture(gl.TEXTURE_2D, this.worldTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);   // the scene is pixel art at the game's own size: never blend its texels
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    this.bloomTex = this.mkTex(gl);
    this.waterTex = this.mkTex(gl);
    // NEAREST for the mask: it carries a DEPTH in a byte, and interpolating
    // between two depths at a shoreline invents a surface that is not there.
    gl.bindTexture(gl.TEXTURE_2D, this.waterTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    // ...and NEAREST for the normals too, and for a sharper reason: a normal
    // is a DIRECTION, and the linear blend of two directions on either side of
    // a boulder's edge points somewhere neither surface faces. Interpolating
    // it rounds every hard edge in the game into a bevel.
    this.normalTex = this.mkTex(gl);
    gl.bindTexture(gl.TEXTURE_2D, this.normalTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    this.bloomFbo = gl.createFramebuffer();
    /* EVERYTHING MADE LAZILY IS FORGOTTEN HERE (Vijay's desktop console after
     * a while: 'context lost', then 'object does not belong to this context'
     * and 'Framebuffer is incomplete' every frame). After a restore this runs
     * again with a NEW context, and a texture kept from the old one -- the
     * height map's, made on first use -- was bound into it; the bloom target
     * is attached by `resize`, which had not run again either. */
    this.heightTex = null; this.heightDirty = true;
    this.hudTex = null; this.uiTex = null;   /* the inked HUD's and screens' textures are made on first use too: kept from a lost context they were bound dead, an INVALID_OPERATION every frame after a restore (LS-494) */
    this.u = {} as Record<string, WebGLUniformLocation | null>;
    for (const n of PRESENT_UNIFORMS) this.u[n] = gl.getUniformLocation(this.prog, n);
    this.ub = {} as Record<string, WebGLUniformLocation | null>;
    for (const n of BLOOM_UNIFORMS) this.ub[n] = gl.getUniformLocation(this.bloomProg, n);
    this.ok = true;
    this.reason = 'webgl2';
    // the bloom target is attached by resize, and resize does its GL work only when `ok`: so after, not before
    if (this.lastResize) this.resize(this.lastResize[0], this.lastResize[1], this.lastResize[2]);
  }

  /** Device pixels per game pixel on the glass, for the world's own drawing size: at most four, two on a touch screen. */
  worldScale() {
    const f = this.fit; if (!f || !f.devW) return 1;
    const k = Math.max(1, Math.round(f.devW / 256));
    const touch = typeof navigator !== 'undefined' && (navigator as any).maxTouchPoints > 0;
    return Math.min(touch ? 2 : 4, k);
  }
  mkTex(gl) {
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    // LINEAR, deliberately: the shader does its own edge work and needs the
    // hardware to interpolate for it. NEAREST here would undo the whole point.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  /** Size the visible canvas. Returns what `fitDevice` decided. */
  lastResize: any = null;
  resize(availCssW, availCssH, dpr) {
    this.lastResize = [availCssW, availCssH, dpr];
    const fit = fitDevice(availCssW, availCssH, dpr, this.buffer.width, this.buffer.height, touchBudget());
    this.visible.width = fit.devW;
    this.visible.height = fit.devH;
    this.visible.style.width = fit.cssW + 'px';
    this.visible.style.height = fit.cssH + 'px';
    if (this.fallback) {
      this.fallback.width = fit.devW;
      this.fallback.height = fit.devH;
      this.fallback.style.width = fit.cssW + 'px';
      this.fallback.style.height = fit.cssH + 'px';
      if (this.ctx2d) this.ctx2d.imageSmoothingEnabled = false;
    }
    this.fit = fit;
    if (this.ok) {
      const gl = this.gl;
      this.bloomW = Math.max(1, Math.floor(this.buffer.width / 2));
      this.bloomH = Math.max(1, Math.floor(this.buffer.height / 2));
      gl.bindTexture(gl.TEXTURE_2D, this.bloomTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.bloomW, this.bloomH, 0,
        gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.bloomFbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D,
        this.bloomTex, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    return fit;
  }

  set(preset) {
    this.opts = { ...PRESET[preset] || PRESET.crisp };
    this.preset = preset;
  }

  /** The water mask for the screen being drawn, or null for a dry one.
   *  `topV` is where the world starts in texture coordinates — the HUD sits
   *  above it and must never appear in a reflection. */
  /** HOW HOT THE GROUND ON THE GLASS IS, 0 to 1: the amount of heat shimmer
   *  the present pass puts through the picture. The game works it out from the
   *  country and the clock and hands it over each frame. */
  setHeat(amount) { this.heat = Math.max(0, Math.min(1, +amount || 0)); }
  // Modal screens share the final composite, but must not inherit its refraction.
  setWorldEffects(visible: boolean) { this.worldEffects = visible; }
  /** The fire pass's strength: 1 as shipped, 0 off (the checks' control). */
  setFireAmount(amount) { this.fireAmt = Math.max(0, Math.min(2, +amount)); }

  setWater(canvas, topV = 0, worldX = 0, worldY = 0, seamV = -1) {
    this.waterMask = canvas || null;
    this.topOfWorld = topV;
    // where two screens meet this frame, so the shoreline mirror does not
    // read one of them into the other
    this.seam = seamV;
    // where this frame sits in the world, so the ripple travels with the lake
    // instead of staying nailed to the glass through a screen transition
    this.waveX = worldX; this.waveY = worldY;
    this.waterDirty = true;
  }

  /* WHAT THE WORLD IS SHAPED LIKE THIS FRAME, and what is shining on it.
   *
   * `normals` is a canvas the same size as the frame in the encoding
   * `light.js` documents; null turns the whole pass off. `lights` is up to
   * MAX_LIGHTS of { x, y, r, i, col } in GAME PIXELS with the HUD already
   * accounted for, because the shader works in the frame's own coordinates
   * and has no idea the top 48 rows are a readout.
   *
   * Deliberately the same shape as `setWater`: the game hands over a baked
   * buffer and some scalars, and the presenter does not ask what any of it
   * means. */
  zoom: any;
  focus: any;
  /** The combat camera: magnify the frame by `z` about `fx, fy` in frame uv
   *  (y up, as the presenter samples). 1 is the whole frame. */
  setZoom(z = 1, fx = 0.5, fy = 0.5) { this.zoom = z; this.focus = [fx, fy]; }
  setLight(normals, lights = [], relief = null, heights = null) {
    this.normalMap = normals || null;
    // the world as a height field, for the shadow march; null skips it
    this.heightMap = heights || null;
    this.heightDirty = true;
    this.lights = lights.slice(0, MAX_LIGHTS);
    this.relief = normals ? (relief || RELIEF_DEFAULT) : null;
    this.normalDirty = true;
  }

  /** THE 3D SCENE'S CANVAS, SAMPLED RATHER THAN COPIED (2026-09-14). Handed this, `draw` uploads it into its own
   *  context and composites the game's canvas over it by alpha; handed null, everything works as it did. */
  setWorldSource(canvas: any) { this.worldSrc = canvas || null; }
  /** THE INKED HUD (2026-09-28, LS-479): a canvas the size of the readout at the world's own scale, or null for the game's strip. */
  hudSrc: any = null; hudTex: WebGLTexture | null = null; uiSrc: any = null; uiTex: WebGLTexture | null = null;
  /** A full-frame inked screen over everything (the title, the bag, the map; 2026-09-28), or null. */
  setUiSource(canvas: any) { this.uiSrc = canvas || null; }
  setHudSource(canvas: any) { this.hudSrc = canvas || null; }

  /** Present one frame. */
  draw() {
    if (!this.ok || !this.gl || this.gl.isContextLost()) return this.blit();   // a lost context draws nothing but errors
    const gl = this.gl;
    const { width: tw, height: th } = this.buffer;
    gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.buffer);
    /* THE WORLD, COMPOSITED HERE (2026-09-14). With a 3D scene handed over by `setWorldSource`, its canvas is uploaded
     * into THIS context and mixed with the game's own canvas in one pass; without one, `srcAll` is simply the game's
     * canvas and nothing below changes. This is what replaced a 30 ms read-back on a throttled phone. */
    let srcAll = this.srcTex;
    if ((this.worldSrc || this.uiSrc) && this.compProg) {
      gl.bindTexture(gl.TEXTURE_2D, this.worldTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.worldSrc || this.buffer);   // no world handed over: the game's own canvas stands in, and the shader's mix gives it back unchanged
      const K = Math.max(1, Math.round(((this.worldSrc || this.uiSrc).width || tw) / tw));   // the world's scale: the composite is made at it, the HUD enlarged by whole pixels
      if (this.compW !== tw * K || this.compH !== th * K) {
        gl.bindTexture(gl.TEXTURE_2D, this.compTex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, tw * K, th * K, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        this.compW = tw * K; this.compH = th * K;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.compFbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.compTex, 0);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.compFbo);
      gl.viewport(0, 0, this.compW, this.compH);
      gl.useProgram(this.compProg);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.worldTex);
      gl.uniform1i(gl.getUniformLocation(this.compProg, 'u_src'), 0);
      gl.uniform1i(gl.getUniformLocation(this.compProg, 'u_world'), 1);
      gl.uniform1f(gl.getUniformLocation(this.compProg, 'u_top'), this.topOfWorld || 0);
      if (this.hudSrc) {
        if (!this.hudTex) { this.hudTex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, this.hudTex); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); }
        gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, this.hudTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.hudSrc);
        gl.uniform1i(gl.getUniformLocation(this.compProg, 'u_hud'), 2);
      }
      gl.uniform1f(gl.getUniformLocation(this.compProg, 'u_hasHud'), this.hudSrc ? 1 : 0);
      if (this.uiSrc) {
        if (!this.uiTex) { this.uiTex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, this.uiTex); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); }
        gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, this.uiTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.uiSrc);
        gl.uniform1i(gl.getUniformLocation(this.compProg, 'u_ui'), 3);
      }
      gl.uniform1f(gl.getUniformLocation(this.compProg, 'u_hasUi'), this.uiSrc ? 1 : 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.activeTexture(gl.TEXTURE0);
      srcAll = this.compTex;
    }

    // --- bloom, at half resolution
    if (this.opts.bloom > 0) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.bloomFbo);
      gl.viewport(0, 0, this.bloomW, this.bloomH);
      gl.useProgram(this.bloomProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, srcAll);
      gl.uniform1i(this.ub.u_src, 0);
      gl.uniform2f(this.ub.u_texel, 1 / this.bloomW, 1 / this.bloomH);
      gl.uniform1f(this.ub.u_threshold, 0.55);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    // --- present
    gl.viewport(0, 0, this.visible.width, this.visible.height);
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcAll);
    gl.uniform1i(this.u.u_src, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.bloomTex);
    gl.uniform1i(this.u.u_bloom, 1);
    gl.uniform2f(this.u.u_texSize, tw, th);
    gl.uniform2f(this.u.u_hiSize, srcAll === this.compTex ? this.compW : tw, srcAll === this.compTex ? this.compH : th);
    gl.uniform2f(this.u.u_outSize, this.visible.width, this.visible.height);
    gl.uniform1f(this.u.u_edge, this.opts.edge);
    gl.uniform1f(this.u.u_bloomAmt, this.opts.bloom);
    gl.uniform1f(this.u.u_grille, this.opts.grille);
    gl.uniform1f(this.u.u_vignette, this.opts.vignette);
    gl.uniform1f(this.u.u_aberration, this.opts.aberration);
    gl.uniform1f(this.u.u_scale, this.fit ? this.fit.scale * this.fit.dpr : 1);
    gl.uniform1f(this.u.u_zoom, this.zoom || 1);
    gl.uniform2f(this.u.u_focus, this.focus ? this.focus[0] : 0.5, this.focus ? this.focus[1] : 0.5);
    // --- the surface
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.waterTex);
    if (this.waterDirty) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      if (this.waterMask) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.waterMask);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
          new Uint8Array([0, 0, 0, 0]));
      }
      this.waterDirty = false;
    }
    gl.uniform1i(this.u.u_water, 2);
    gl.uniform1f(this.u.u_wet, this.waterMask ? (this.opts.wet ?? 1) : 0);
    gl.uniform1f(this.u.u_heat, this.worldEffects ? this.heat || 0 : 0);
    gl.uniform1f(this.u.u_inkLook, typeof location !== 'undefined' && /[?&]pixelfloor=1/.test(location.search) ? 0 : 1);
    { // the fires on the glass: the lights the game tagged as fire, in the same frame pixels the relief pass reads them in
      const F = this.worldEffects ? (this.lights || []).filter((l) => l && l.fire).slice(0, MAX_LIGHTS) : [];
      gl.uniform1i(this.u.u_fireN, F.length);
      if (F.length) { const arr = new Float32Array(F.length * 4); for (let i = 0; i < F.length; i++) { arr[i * 4] = F[i].x; arr[i * 4 + 1] = F[i].y; arr[i * 4 + 2] = F[i].r; arr[i * 4 + 3] = F[i].i; } gl.uniform4fv(this.u.u_firePos, arr); }
      gl.uniform1f(this.u.u_fireAmt, this.fireAmt === undefined ? 1 : this.fireAmt);
    }
    gl.uniform1f(this.u.u_time, (this.frame = (this.frame || 0) + 1) / 60);
    gl.uniform1f(this.u.u_top, this.topOfWorld || 0);
    { const G: any = globalThis as any; const K = Number.isFinite(G.__perspK) ? G.__perspK : (this.perspK || 0), T = Number.isFinite(G.__perspT) ? G.__perspT : (this.perspT || 0);
      gl.uniform1f(this.u.u_persp, K); gl.uniform1f(this.u.u_pitch, T);
      gl.uniform2f(this.u.u_perspC, 0.5, (1 - (this.topOfWorld || 0)) / 2); }
    gl.uniform2f(this.u.u_wave, this.waveX || 0, this.waveY || 0);
    gl.uniform1f(this.u.u_seam, this.seam === undefined ? -1 : this.seam);
    // --- the relief
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.normalTex);
    if (this.normalDirty) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      if (this.normalMap) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.normalMap);
      } else {
        // one flat texel, so a frame with no relief samples something valid
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
          new Uint8Array([128, 128, 255, 255]));
      }
      this.normalDirty = false;
    }
    gl.uniform1i(this.u.u_normal, 3);
    // --- the height field the shadows are marched through
    if (!this.heightTex) {
      this.heightTex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE4);
      gl.bindTexture(gl.TEXTURE_2D, this.heightTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.heightDirty = true;
    }
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.heightTex);
    if (this.heightDirty) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      if (this.heightMap) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.heightMap);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
          new Uint8Array([0, 0, 0, 255]));
      }
      this.heightDirty = false;
    }
    gl.uniform1i(this.u.u_height, 4);
    gl.uniform1f(this.u.u_shadow, this.heightMap ? (this.relief && this.relief.shadow !== undefined ? this.relief.shadow : 0) : 0);
    gl.uniform1f(this.u.u_rt, this.heightMap ? (this.relief && this.relief.rt !== undefined ? this.relief.rt : 0) : 0);
    const R = this.relief;
    gl.uniform1f(this.u.u_relief, R ? (R.relief ?? 1) : 0);
    if (R) {
      gl.uniform1f(this.u.u_ambient, R.ambient);
      gl.uniform1f(this.u.u_sunAmt, R.sunAmt);
      gl.uniform3f(this.u.u_sun, R.sun[0], R.sun[1], R.sun[2]);
      const tint = R.tint || [1, 1, 1];
      gl.uniform1f(this.u.u_hud, R.hud || 0);
      gl.uniform3f(this.u.u_tint, tint[0], tint[1], tint[2]);
      const L = this.lights || [];
      gl.uniform1i(this.u.u_lightN, L.length);
      if (L.length) {
        // Flat arrays, uploaded once: a uniform1f per light per frame is 64
        // GL calls for something the driver will take in one.
        const pos = new Float32Array(L.length * 4), col = new Float32Array(L.length * 3);
        for (let i = 0; i < L.length; i++) {
          pos[i * 4] = L[i].x; pos[i * 4 + 1] = L[i].y;
          pos[i * 4 + 2] = L[i].r; pos[i * 4 + 3] = L[i].i;
          col[i * 3] = L[i].col[0]; col[i * 3 + 1] = L[i].col[1]; col[i * 3 + 2] = L[i].col[2];
        }
        gl.uniform4fv(this.u.u_lightPos, pos);
        gl.uniform3fv(this.u.u_lightCol, col);
      }
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    return true;
  }

  /** No WebGL, or it went away: paint the buffer straight on, which is
   *  precisely the behaviour this file replaced. */
  /* THE FALLBACK COULD NOT FALL BACK.
   *
   * This asked the visible canvas for a `2d` context — and a canvas that has
   * ever held a WebGL context returns `null` from `getContext('2d')`, forever,
   * by specification. So the header's promise that "a context loss loses the
   * effects and nothing else" was false in exactly the case it was written
   * for. Measured by an examiner: after `loseContext()`, 1,296,800 of 1.31M
   * pixels pure white, `game.mode` still `'play'`, input still accepted, no
   * recovery without a reload. On a backgrounded iOS tab this is routine.
   *
   * A canvas can only ever have one kind of context, so the answer is a
   * second canvas: created on demand, laid over the dead one at the same
   * size and position, and thrown away if the context comes back.
   */
  blit() {
    if (!this.ctx2d) {
      // the ordinary case first: a canvas that never had a GL context
      try { this.ctx2d = this.visible.getContext('2d'); } catch (err) { this.ctx2d = null; }
      if (!this.ctx2d) this.ctx2d = this.makeFallback();
      if (!this.ctx2d) return false;
      this.ctx2d.imageSmoothingEnabled = false;
    }
    const c = this.fallback || this.visible;
    this.ctx2d.drawImage(this.buffer, 0, 0, c.width, c.height);
    return true;
  }

  /** A second canvas, over the dead one, in the same box. */
  makeFallback() {
    const v = this.visible;
    if (!v || typeof document === 'undefined' || !v.parentNode) return null;
    const c = document.createElement('canvas');
    c.width = v.width; c.height = v.height;
    c.className = v.className;
    c.style.cssText = v.style.cssText;
    // it has to sit exactly where the original does; the original keeps its
    // place in the layout and stops being painted
    v.style.visibility = 'hidden';
    v.parentNode.insertBefore(c, v.nextSibling);
    this.fallback = c;
    let g = null;
    try { g = c.getContext('2d'); } catch (err) { g = null; }
    return g;
  }
}
